/**
 * InvitePage — public landing for merchant invitations (B4).
 *
 * /invite?token=<signed invite token>. The token is decoded CLIENT-SIDE for
 * display only (email + expiry — no verification here); all real validation
 * happens server-side in auth-google-callback when the token rides the OAuth
 * state param. The CTA requires signing in with the INVITED mailbox's Google
 * account — the server enforces the email match.
 */

import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Card, Title, Text } from '@nimbus-ds/components'
import { authService } from '@/api/auth'

interface InvitePayload {
  email?: string
  store_id?: string
  exp?: number
  purpose?: string
}

function decodePayload(token: string | null): InvitePayload | null {
  if (!token) return null
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json)
  } catch {
    return null
  }
}

export function InvitePage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const payload = useMemo(() => decodePayload(token), [token])
  const expired = !!payload?.exp && payload.exp * 1000 < Date.now()
  const valid = !!token && payload?.purpose === 'invite' && !expired

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <Card.Header className="text-center">
            <Title as="h2">
              {valid ? 'Te invitaron a tu panel' : 'Invitación no válida'}
            </Title>
          </Card.Header>
          <Card.Body>
            {valid ? (
              <div className="space-y-4 text-center">
                <Text>
                  Vas a poder ver las métricas y pruebas de tu tienda. Ingresá con la cuenta de
                  Google de <strong>{payload?.email}</strong> — sin contraseñas nuevas.
                </Text>
                <Button
                  appearance="primary"
                  fullWidth
                  onClick={() => authService.startGoogleLogin({ invite: token! })}
                >
                  Ingresar con Google
                </Button>
                <Text fontSize="caption">
                  La invitación es personal: solo funciona con esa cuenta de correo.
                </Text>
              </div>
            ) : (
              <div className="space-y-3 text-center">
                <Text>
                  {expired
                    ? 'Esta invitación venció. Pedile a quien te invitó que te envíe una nueva.'
                    : 'El enlace no es válido o está incompleto. Revisá que lo hayas copiado entero, o pedí una invitación nueva.'}
                </Text>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}
